import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  Paper,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
  Button,
  Grid2,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
} from "@mui/icons-material";
import { TimePicker } from "@mui/x-date-pickers";

const ScheduleTimesWidget = (props) => {
  const { formData, onChange, formContext } = props;
  const { scheduleVariableFlexible } = formContext?.formData || {};
  const selectedScheduleType = scheduleVariableFlexible?.scheduleType || "";
  const [copiedTime, setCopiedTime] = useState(null);
  const [showOverrideCard, setShowOverrideCard] = useState(false);
  const [overrideDismissed, setOverrideDismissed] = useState(false);
  const [timeErrors, setTimeErrors] = useState({});
  const [lastScheduleType, setLastScheduleType] = useState(
    scheduleVariableFlexible?.scheduleType
  );
  const allDays = useMemo(
    () => [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    []
  );

  const scheduleDayMap = useMemo(
    () => ({
      "24/7 Round-the-Clock Care": allDays,
      "Live-in Care": allDays,
      Custom: allDays,
    }),
    [allDays]
  );

  const selectedDays = useMemo(
    () => scheduleDayMap[selectedScheduleType] || [],
    [scheduleDayMap, selectedScheduleType]
  );

  const dayTimes = formData?.dayTimes || {};

  const handleTimeChange = (day, field, value) => {
    const updatedDayTimes = { ...dayTimes };
    let updatedStartTime = updatedDayTimes[day]?.startTime || "";
    let updatedEndTime = updatedDayTimes[day]?.endTime || "";
    const newErrors = { ...(timeErrors[day] || {}) };

    if (field === "startTime") {
      updatedStartTime = value;
      if (!value) newErrors.startTime = "Start time is required";
      else delete newErrors.startTime;

      if (updatedEndTime && value && updatedEndTime <= value) {
        newErrors.endTime = "End time must be later than start time";
      } else if (updatedEndTime) {
        delete newErrors.endTime;
      }
    }

    if (field === "endTime") {
      updatedEndTime = value;
      if (!value) newErrors.endTime = "End time is required";
      else if (updatedStartTime && value <= updatedStartTime) {
        newErrors.endTime = "End time must be later than start time";
      } else {
        delete newErrors.endTime;
      }
    }

    updatedDayTimes[day] = {
      startTime: updatedStartTime,
      endTime: updatedEndTime,
    };

    setTimeErrors((prev) => ({
      ...prev,
      [day]: Object.keys(newErrors).length ? newErrors : undefined,
    }));

    if (Object.keys(newErrors).length === 0) {
      onChange({ ...formData, dayTimes: updatedDayTimes });
    }
  };

  const handleCopy = (day) => {
    const time = dayTimes[day];
    setCopiedTime(time);
  };

  const handlePaste = (day) => {
    if (copiedTime) {
      const newDayTimes = {
        ...dayTimes,
        [day]: { ...copiedTime },
      };
      onChange({ ...formData, dayTimes: newDayTimes });
    }
  };

  const handleChangeClick = () => {
    setShowOverrideCard(false);
    setOverrideDismissed(true);
  };

  useEffect(() => {
    let validDays =
      Array.isArray(formData?.days) && formData?.days.length > 0
        ? formData.days
        : selectedDays;
    if (
      selectedScheduleType !== "" &&
      lastScheduleType !== selectedScheduleType
    ) {
      validDays = selectedDays;
    }
    const showTimesValue =
      selectedScheduleType !== "24/7 Round-the-Clock Care" &&
      selectedScheduleType !== "Live-in Care";

    const fixedScheduleTypes = [
      "24/7 Round-the-Clock Care",
      "Live-in Care",
      "Custom",
    ];

    const isFixed = fixedScheduleTypes.includes(selectedScheduleType);
    const isTypeChanged = selectedScheduleType !== lastScheduleType;
    if (isFixed && isTypeChanged) {
      onChange({
        ...formData,
        days: [...validDays],
        showTimes: showTimesValue,
      });
    } else if (formData?.showTimes !== showTimesValue) {
      onChange({
        ...formData,
        showTimes: showTimesValue,
      });
    } else if (
      !fixedScheduleTypes.includes(selectedScheduleType) &&
      lastScheduleType
    ) {
      if (selectedScheduleType !== lastScheduleType && selectedScheduleType) {
        formContext.setFormData?.((prev) => ({
          ...prev,
          scheduleVariableFlexible: {
            ...prev.scheduleVariableFlexible,
            scheduleType: "Custom",
          },
        }));
        formContext.setFormDataVersion?.(formContext.formDataVersion + 1);
      } else if (!selectedScheduleType) {
        formContext.setFormData?.((prev) => ({
          ...prev,
          scheduleVariableFlexible: {
            ...prev.scheduleVariableFlexible,
            scheduleType: "",
            days: [""],
            showTimes: true,
            dayTimes: {},
          },
        }));
        formContext.setFormDataVersion?.(formContext.formDataVersion + 1);
      }
    }

    if (!overrideDismissed) {
      setShowOverrideCard(
        selectedScheduleType === "24/7 Round-the-Clock Care" ||
          selectedScheduleType === "Live-in Care"
      );
    }
    setLastScheduleType(selectedScheduleType);
  }, [
    selectedScheduleType,
    selectedDays,
    formData?.showTimes,
    scheduleVariableFlexible,
  ]);

  useEffect(() => {
    setOverrideDismissed(false);
  }, [selectedScheduleType]);

  useEffect(() => {
    const updatedDayTimes = { ...formData?.dayTimes };
    let changed = false;

    (formData?.days || []).forEach((day) => {
      if (!updatedDayTimes?.[day]) {
        updatedDayTimes[day] = {
          startTime: "09:00",
          endTime: "17:00",
        };
        changed = true;
      }
    });

    if (changed) {
      onChange({ ...formData, dayTimes: updatedDayTimes });
    }
  }, [formData?.days]);
  return (
    <>
      {showOverrideCard && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#ffebee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2">
            {selectedScheduleType === "24/7 Round-the-Clock Care"
              ? "Full-time caregiver with rotating caregivers providing 24-hour coverage"
              : "Caregiver lives in the home, providing extended care with daily rest periods"}
          </Typography>
          <Button variant="outlined" size="small" onClick={handleChangeClick}>
            Change
          </Button>
        </Paper>
      )}

      {/* Day Checkboxes */}
      <Typography variant="h6" color="default.main" mb={1}>
        Select Days
      </Typography>
      <Grid2 container spacing={1} mb={1}>
        {allDays.map((day) => (
          <Grid2 item size={{ xs: "auto" }} key={day}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData?.days?.includes(day) || false}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    let newDays;
                    let newDayTimes = { ...formData?.dayTimes };
                    if (isChecked) {
                      newDays = [...(formData?.days || []), day];
                      // Add default times if not present
                      if (!newDayTimes[day]) {
                        newDayTimes[day] = {
                          startTime: "09:00",
                          endTime: "17:00",
                        };
                      }
                    } else {
                      newDays = (formData?.days || []).filter((d) => d !== day);
                      // Remove its times too
                      delete newDayTimes[day];
                    }

                    onChange({
                      ...formData,
                      days: newDays,
                      dayTimes: newDayTimes,
                    });
                  }}
                  color="primary"
                />
              }
              label={day}
            />
          </Grid2>
        ))}
      </Grid2>

      {/* Time Inputs */}
      {!showOverrideCard && (
        <Grid2 container spacing={3} mt={1}>
          {allDays
            .filter((day) => formData?.days?.includes(day))
            .map((day) => (
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" color="default.main" mb={2}>
                  {day}
                </Typography>
                <Grid2 container spacing={2} alignItems="center">
                  <Grid2
                    item
                    size={{ xs: 8, sm: 9 }}
                    display="flex"
                    alignItems="center"
                    flexWrap={{ xs: "wrap", sm: "nowrap" }}
                    gap={2}
                  >
                    <TimePicker
                      label="Start"
                      value={
                        dayTimes[day]?.startTime
                          ? dayjs(dayTimes[day].startTime, "HH:mm")
                          : null
                      }
                      onChange={(value) => {
                        handleTimeChange(
                          day,
                          "startTime",
                          value?.format("HH:mm") || ""
                        );
                      }}
                      ampm
                      timeSteps={{ minutes: 1 }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: Boolean(timeErrors[day]?.startTime),
                          helperText: timeErrors[day]?.startTime,
                        },
                      }}
                    />
                    <TimePicker
                      label="End"
                      value={
                        dayTimes[day]?.endTime
                          ? dayjs(dayTimes[day].endTime, "HH:mm")
                          : null
                      }
                      onChange={(value) => {
                        handleTimeChange(
                          day,
                          "endTime",
                          value?.format("HH:mm") || ""
                        );
                      }}
                      ampm
                      timeSteps={{ minutes: 1 }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: Boolean(timeErrors[day]?.endTime),
                          helperText: timeErrors[day]?.endTime,
                        },
                      }}
                    />
                  </Grid2>
                  <Grid2 item size={{ xs: 4, sm: 3 }}>
                    <IconButton
                      onClick={() => handleCopy(day)}
                      color="primary"
                      size="small"
                      title="Copy these times"
                      sx={{ flexDirection: "column" }}
                    >
                      <CopyIcon />
                      <Typography variant="subtitle2">Copy</Typography>
                    </IconButton>

                    <IconButton
                      onClick={() => handlePaste(day)}
                      color="primary"
                      size="small"
                      title="Use copied times"
                      disabled={!copiedTime}
                      sx={{ flexDirection: "column" }}
                    >
                      <PasteIcon />
                      <Typography variant="subtitle2">Paste</Typography>
                    </IconButton>
                  </Grid2>
                </Grid2>
              </Grid2>
            ))}
        </Grid2>
      )}
    </>
  );
};

export default ScheduleTimesWidget;
